import { memo, lazy, Suspense, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import OptimizedImage from "@/components/OptimizedImage";
import { useSiteTheme } from "@/hooks/useSiteTheme";
import { useShopCategories, type Category } from "@/hooks/useCategories";
import { useIsMobile } from "@/hooks/use-mobile";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import toysImage from "@/assets/baby-toys.jpg";
import feedingImage from "@/assets/baby-feeding.jpg";
import diapersImage from "@/assets/baby-diapers.jpg";
import careImage from "@/assets/baby-care.jpg";
import clothingImage from "@/assets/baby-clothing.jpg";
import accessoriesImage from "@/assets/baby-accessories.jpg";
import newbornImage from "@/assets/baby-newborn.jpg";


const ShopByCollections = lazy(() => import("@/components/ShopByCollections"));

const CATEGORY_FALLBACK_IMAGES = [toysImage, feedingImage, diapersImage, careImage, clothingImage, accessoriesImage, newbornImage];

const getCategoryImage = (cat: Category, index = 0) => {
  if (cat.image && cat.image !== "/placeholder.svg") return cat.image;

  const key = `${cat.name} ${cat.slug}`.toLowerCase();
  if (key.includes("diaper") || key.includes("pamper")) return diapersImage;
  if (key.includes("feed") || key.includes("bottle")) return feedingImage;
  if (key.includes("care") || key.includes("lotion")) return careImage;
  if (key.includes("cloth") || key.includes("dress") || key.includes("wear")) return clothingImage;
  if (key.includes("accessor") || key.includes("cap") || key.includes("bib")) return accessoriesImage;
  if (key.includes("newborn")) return newbornImage;
  return CATEGORY_FALLBACK_IMAGES[index % CATEGORY_FALLBACK_IMAGES.length];
};

const SIZE_MAP: Record<string, { circle: string; circleImg: number; text: string }> = {
  small: { circle: "w-16 h-16 md:w-24 md:h-24", circleImg: 192, text: "text-[9px] md:text-[10px]" },
  medium: { circle: "w-20 h-20 md:w-36 md:h-36", circleImg: 288, text: "text-[10px] md:text-xs" },
  large: { circle: "w-28 h-28 md:w-44 md:h-44", circleImg: 352, text: "text-xs md:text-sm" },
};

// ─── Template-based layout configs for layouts 11-50 ───
interface LayoutTemplate {
  container: string;
  itemWrapper: string;
  aspect: string;
  overlayGradient: string;
  textPos: "bl" | "center" | "bc" | "tl" | "tr" | "br";
  showCta: boolean;
  shape: "square" | "rounded" | "circle" | "pill" | "hexagon" | "diamond";
  cols: string;
}

const TEMPLATE_LAYOUTS: Record<string, LayoutTemplate> = {
  hexagons:       { container: "grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6", itemWrapper: "group flex flex-col items-center gap-2", aspect: "aspect-square", overlayGradient: "none", textPos: "bc", showCta: false, shape: "hexagon", cols: "6" },
  pills:          { container: "flex flex-wrap justify-center gap-3 md:gap-5", itemWrapper: "group", aspect: "aspect-[2/1]", overlayGradient: "to-r from-black/60 to-transparent", textPos: "bl", showCta: false, shape: "pill", cols: "auto" },
  diamonds:       { container: "grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8", itemWrapper: "group flex flex-col items-center gap-3", aspect: "aspect-square", overlayGradient: "none", textPos: "bc", showCta: false, shape: "diamond", cols: "5" },
  polaroid:       { container: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6", itemWrapper: "group", aspect: "aspect-[3/4]", overlayGradient: "to-t from-black/50 to-transparent", textPos: "bc", showCta: false, shape: "square", cols: "4" },
  filmstrip:      { container: "flex gap-3 md:gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory", itemWrapper: "group flex-shrink-0 w-36 md:w-52 snap-start", aspect: "aspect-[3/4]", overlayGradient: "to-t from-black/60 via-transparent to-transparent", textPos: "bl", showCta: true, shape: "square", cols: "auto" },
  "masonry-alt":  { container: "grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4", itemWrapper: "group", aspect: "aspect-auto", overlayGradient: "to-t from-black/60 to-transparent", textPos: "bl", showCta: false, shape: "rounded", cols: "3" },
  "accordion-list": { container: "max-w-3xl mx-auto space-y-2", itemWrapper: "group", aspect: "h-16 md:h-20", overlayGradient: "to-r from-black/70 via-black/30 to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "1" },
  zigzag:         { container: "space-y-4 md:space-y-6", itemWrapper: "group", aspect: "h-28 md:h-40", overlayGradient: "to-r from-black/70 via-black/30 to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "1" },
  "overlay-center": { container: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5", itemWrapper: "group", aspect: "aspect-square", overlayGradient: "to-t from-black/70 via-black/30 to-black/10", textPos: "center", showCta: false, shape: "rounded", cols: "4" },
  "stacked-cards": { container: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7", itemWrapper: "group", aspect: "aspect-[3/4]", overlayGradient: "to-t from-black/60 to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "4" },
  newspaper:      { container: "grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4", itemWrapper: "group", aspect: "aspect-[4/5]", overlayGradient: "to-t from-black/70 via-black/20 to-transparent", textPos: "bl", showCta: false, shape: "square", cols: "4" },
  metro:          { container: "grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-3 auto-rows-[100px] md:auto-rows-[140px]", itemWrapper: "group", aspect: "auto", overlayGradient: "to-t from-black/60 to-transparent", textPos: "bl", showCta: false, shape: "square", cols: "4" },
  collage:        { container: "grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-3", itemWrapper: "group", aspect: "aspect-square", overlayGradient: "to-b from-transparent via-transparent to-black/70", textPos: "bc", showCta: false, shape: "rounded", cols: "5" },
  ribbon:         { container: "space-y-3 md:space-y-4 max-w-4xl mx-auto", itemWrapper: "group", aspect: "h-20 md:h-28", overlayGradient: "to-r from-black/75 via-black/40 to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "1" },
  spotlight:      { container: "space-y-4", itemWrapper: "group", aspect: "auto", overlayGradient: "to-r from-black/80 via-black/40 to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "mixed" },
  shelf:          { container: "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5", itemWrapper: "group text-center", aspect: "aspect-[4/5]", overlayGradient: "none", textPos: "bc", showCta: false, shape: "rounded", cols: "5" },
  bubble:         { container: "flex flex-wrap justify-center gap-4 md:gap-6", itemWrapper: "group flex flex-col items-center gap-2", aspect: "aspect-square", overlayGradient: "none", textPos: "bc", showCta: false, shape: "circle", cols: "auto" },
  wave:           { container: "flex gap-4 md:gap-6 overflow-x-auto pb-4 -mx-4 px-4", itemWrapper: "group flex-shrink-0 w-32 md:w-44", aspect: "aspect-[3/4]", overlayGradient: "to-t from-black/60 to-transparent", textPos: "bl", showCta: false, shape: "rounded", cols: "auto" },
  "glass-cards":  { container: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6", itemWrapper: "group", aspect: "aspect-square", overlayGradient: "to-t from-black/40 via-black/10 to-transparent", textPos: "center", showCta: true, shape: "rounded", cols: "4" },
  vintage:        { container: "grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6", itemWrapper: "group", aspect: "aspect-[3/4]", overlayGradient: "to-t from-black/60 via-transparent to-black/5", textPos: "bc", showCta: false, shape: "square", cols: "3" },
  catalog:        { container: "grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5", itemWrapper: "group", aspect: "aspect-[3/4]", overlayGradient: "to-t from-black/65 to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "4" },
  runway:         { container: "flex gap-3 md:gap-5 overflow-x-auto pb-4 -mx-4 px-4 snap-x", itemWrapper: "group flex-shrink-0 w-44 md:w-64 snap-start", aspect: "aspect-[2/3]", overlayGradient: "to-t from-black/70 via-black/20 to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "auto" },
  "gallery-wall": { container: "grid grid-cols-3 md:grid-cols-4 gap-3 md:gap-4", itemWrapper: "group", aspect: "aspect-square", overlayGradient: "to-t from-black/50 to-transparent", textPos: "bc", showCta: false, shape: "square", cols: "4" },
  "tag-wall":     { container: "flex flex-wrap justify-center gap-2 md:gap-3", itemWrapper: "group", aspect: "auto", overlayGradient: "none", textPos: "center", showCta: false, shape: "pill", cols: "auto" },
  brick:          { container: "grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 auto-rows-[110px] md:auto-rows-[160px]", itemWrapper: "group", aspect: "auto", overlayGradient: "to-t from-black/60 to-transparent", textPos: "bl", showCta: false, shape: "square", cols: "3" },
  fan:            { container: "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4", itemWrapper: "group", aspect: "aspect-[3/4]", overlayGradient: "to-t from-black/70 to-transparent", textPos: "bl", showCta: false, shape: "rounded", cols: "5" },
  "window-pane":  { container: "grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6", itemWrapper: "group", aspect: "aspect-[4/3]", overlayGradient: "to-t from-black/55 via-transparent to-transparent", textPos: "bl", showCta: true, shape: "square", cols: "3" },
  cinema:         { container: "space-y-2 md:space-y-3", itemWrapper: "group", aspect: "h-24 md:h-36", overlayGradient: "to-r from-black/80 via-black/40 to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "1" },
  postcard:       { container: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7", itemWrapper: "group", aspect: "aspect-[4/3]", overlayGradient: "none", textPos: "bc", showCta: false, shape: "square", cols: "4" },
  marquee:        { container: "flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x", itemWrapper: "group flex-shrink-0 w-40 md:w-56 snap-start", aspect: "aspect-square", overlayGradient: "to-t from-black/65 to-transparent", textPos: "bc", showCta: true, shape: "rounded", cols: "auto" },
  "split-text":   { container: "space-y-3 md:space-y-4 max-w-4xl mx-auto", itemWrapper: "group", aspect: "h-24 md:h-32", overlayGradient: "to-r from-black/70 via-black/30 to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "1" },
  "vertical-scroll": { container: "flex gap-4 md:gap-6 overflow-x-auto pb-4 -mx-4 px-4", itemWrapper: "group flex-shrink-0 w-36 md:w-48", aspect: "aspect-[2/3]", overlayGradient: "to-t from-black/70 via-black/30 to-transparent", textPos: "bl", showCta: false, shape: "rounded", cols: "auto" },
  orbit:          { container: "grid grid-cols-3 md:grid-cols-5 gap-4 md:gap-6", itemWrapper: "group flex flex-col items-center gap-2", aspect: "aspect-square", overlayGradient: "none", textPos: "bc", showCta: false, shape: "circle", cols: "5" },
  cascade:        { container: "grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5", itemWrapper: "group", aspect: "aspect-[3/4]", overlayGradient: "to-b from-transparent to-black/70", textPos: "bc", showCta: true, shape: "rounded", cols: "3" },
  chevron:        { container: "grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4", itemWrapper: "group", aspect: "aspect-[4/5]", overlayGradient: "to-t from-black/70 via-transparent to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "4" },
  panel:          { container: "grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5", itemWrapper: "group", aspect: "h-40 md:h-56", overlayGradient: "to-r from-black/75 via-black/35 to-transparent", textPos: "bl", showCta: true, shape: "rounded", cols: "2" },
  "tile-flip":    { container: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5", itemWrapper: "group", aspect: "aspect-square", overlayGradient: "to-t from-black/60 via-black/20 to-transparent", textPos: "center", showCta: false, shape: "rounded", cols: "4" },
  arch:           { container: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7", itemWrapper: "group text-center", aspect: "aspect-[3/4]", overlayGradient: "to-t from-black/50 to-transparent", textPos: "bc", showCta: false, shape: "rounded", cols: "4" },
  book:           { container: "grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5", itemWrapper: "group", aspect: "aspect-[2/3]", overlayGradient: "to-t from-black/65 via-transparent to-transparent", textPos: "bl", showCta: true, shape: "square", cols: "4" },
  stamp:          { container: "grid grid-cols-3 md:grid-cols-5 gap-3 md:gap-4", itemWrapper: "group flex flex-col items-center gap-2", aspect: "aspect-square", overlayGradient: "none", textPos: "bc", showCta: false, shape: "square", cols: "5" },
};

interface CategoriesProps {
  enabled?: boolean;
}

const Categories = memo(({ enabled = true }: CategoriesProps) => {
  const { t } = useLanguage();
  const { theme } = useSiteTheme();
  const location = useLocation();

  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const previewCategory = urlParams.get("preview_category");
  const layout = previewCategory || theme.categoryLayout;
  const { data: allCategories = [], isLoading } = useShopCategories(enabled && layout !== "collections-tab");
  const isMobile = useIsMobile();
  const [showAll, setShowAll] = useState(false);
  const MOBILE_LIMIT = 6;
  const DESKTOP_LIMIT = 8;
  const limit = isMobile ? MOBILE_LIMIT : DESKTOP_LIMIT;
  const hasMore = allCategories.length > limit;
  const categories = hasMore && !showAll ? allCategories.slice(0, limit) : allCategories;
  const mobilePreviewCategories = categories;
  const desktopPreviewCategories = categories;

  // Special: collections-tab layout renders ShopByCollections instead
  if (layout === "collections-tab") {
    return (
      <div id="categories">
        <Suspense fallback={<div className="py-20 flex justify-center"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
          <ShopByCollections />
        </Suspense>
      </div>
    );
  }

  if (isLoading) {
    return (
      <section id="categories" className="py-6 md:py-10 lg:py-12 mx-2 sm:mx-4 my-2 sm:my-3 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[hsl(var(--baby-mint))] via-[hsl(var(--baby-cream))] to-[hsl(var(--baby-pink))]" style={{ minHeight: "420px", contain: "layout style" }}>
        <div className="mx-auto max-w-[1800px] px-4">
          <div className="text-center mb-8 md:mb-14">
            <div className="h-3 w-28 mx-auto rounded" style={{ backgroundColor: `hsl(${theme.mutedText} / 0.12)` }} />
            <div className="h-9 w-48 mx-auto mt-3 rounded" style={{ backgroundColor: `hsl(${theme.categoryText} / 0.1)` }} />
            <div className="w-24 mx-auto mt-4 h-px" style={{ background: `linear-gradient(90deg, transparent, hsl(${theme.cardAccent} / 0.25), transparent)` }} />
          </div>
          <div className="grid grid-cols-3 gap-4 md:flex md:flex-wrap md:justify-center md:gap-8 lg:gap-12">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="flex flex-col items-center gap-2.5 md:gap-3">
                <div className="w-20 h-20 md:w-36 md:h-36 rounded-lg" style={{ backgroundColor: `hsl(${theme.cardText} / 0.08)` }} />
                <div className="h-3 w-16 rounded" style={{ backgroundColor: `hsl(${theme.mutedText} / 0.12)` }} />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (allCategories.length === 0) return null;

  const getSize = (cat: Category) => (cat.display_size as string) || "medium";

  const sectionHeader = (
    <div className="text-center mb-4 md:mb-6">
      <h2 className="font-heading italic text-2xl md:text-3xl lg:text-4xl leading-tight mb-2" style={{ color: `hsl(${theme.categoryText})` }}>
        {t("home_categories_title")}
      </h2>
      <div className="flex items-center justify-center gap-2">
        <span className="h-px w-8 md:w-12" style={{ backgroundColor: `hsl(${theme.cardAccent} / 0.4)` }} />
        <span className="w-1.5 h-1.5 rotate-45 border" style={{ borderColor: `hsl(${theme.cardAccent})` }} />
        <span className="h-px w-8 md:w-12" style={{ backgroundColor: `hsl(${theme.cardAccent} / 0.4)` }} />
      </div>
    </div>
  );

  const renderPreviewItem = (cat: Category, index = 0) => (
    <Link key={cat.id} to={`/category/${cat.slug}`} className="group flex min-w-0 flex-col items-center gap-3 md:gap-3.5">
      <div
        className="relative aspect-square w-full max-w-[6rem] overflow-hidden rounded-2xl transition-all duration-500 group-hover:-translate-y-1 md:max-w-[7rem] lg:max-w-[7.75rem] xl:max-w-[8.5rem]"
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid hsl(0 0% 100%)",
          boxShadow: "none",
        }}
      >
        <OptimizedImage
          src={getCategoryImage(cat, index)}
          alt={cat.name}
          width={256}
          height={256}
          quality={85}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
        />
        <span className="pointer-events-none absolute inset-0 rounded-2xl" style={{ border: "1px solid hsl(0 0% 100% / 0.6)" }} />
      </div>
      <span className="max-w-full text-center font-body font-semibold text-[9px] md:text-[10px] uppercase leading-tight tracking-[0.18em] transition-colors duration-300" style={{ color: "hsl(0 0% 12%)" }}>
        {cat.name}
      </span>
    </Link>
  );

  const compactPreview = (
    <>
      <div className="grid grid-cols-3 gap-4 md:hidden">
        {mobilePreviewCategories.map((cat, index) => renderPreviewItem(cat, index))}
      </div>
      <div className="hidden md:grid md:grid-cols-8 md:items-start md:gap-4 lg:gap-5">
        {desktopPreviewCategories.map((cat, index) => renderPreviewItem(cat, index))}
      </div>
    </>
  );

  // ─── Template-based renderer for layouts 11-50 ───
  const renderTemplateLayout = (tpl: LayoutTemplate) => {
    const getShapeClass = () => {
      switch (tpl.shape) {
        case "circle": return "rounded-full";
        case "pill": return "rounded-full";
        case "hexagon": return "rounded-2xl";
        case "diamond": return "rounded-lg";
        case "rounded": return "rounded-xl";
        default: return "rounded-md";
      }
    };

    const getTextPosClass = () => {
      switch (tpl.textPos) {
        case "center": return "absolute inset-0 flex items-center justify-center";
        case "bc": return "absolute bottom-0 left-0 right-0 flex justify-center pb-3 md:pb-4";
        case "tl": return "absolute top-2 left-2 md:top-3 md:left-3";
        case "tr": return "absolute top-2 right-2 md:top-3 md:right-3";
        case "br": return "absolute bottom-2 right-2 md:bottom-3 md:right-3";
        default: return "absolute bottom-0 left-0 right-0 p-2 md:p-4";
      }
    };

    const shapeClass = getShapeClass();
    const isNoOverlay = tpl.overlayGradient === "none";
    const isHorizontal = tpl.aspect.startsWith("h-");
    const isDiamond = tpl.shape === "diamond";
    const isTagWall = layout === "tag-wall";
    const isSpotlight = layout === "spotlight";

    // Special: tag-wall renders as simple tag buttons
    if (isTagWall) {
      return (
        <div className={tpl.container}>
          {categories.map((cat, index) => (
            <Link key={cat.id} to={`/category/${cat.slug}`} className="group inline-flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 transition-all duration-300" style={{ border: `1px solid hsl(${theme.borderColor})`, borderRadius: "9999px", color: `hsl(${theme.categoryText})` }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = `hsl(${theme.cardAccent})`; e.currentTarget.style.backgroundColor = `hsl(${theme.cardAccent} / 0.1)`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = `hsl(${theme.borderColor})`; e.currentTarget.style.backgroundColor = "transparent"; }}
            >
              {cat.image && <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0"><OptimizedImage src={cat.image} alt={cat.name} width={48} height={48} className="w-full h-full object-cover" /></div>}
              <span className="text-xs md:text-sm font-body tracking-wide">{cat.name}</span>
            </Link>
          ))}
        </div>
      );
    }

    // Special: spotlight renders first item large, rest small
    if (isSpotlight) {
      return (
        <div className="space-y-4">
          {categories.length > 0 && (
            <Link to={`/category/${categories[0].slug}`} className="group block relative overflow-hidden rounded-xl h-48 md:h-72">
              <OptimizedImage src={getCategoryImage(categories[0], 0)} alt={categories[0].name} width={1200} height={500} quality={85} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
              <div className="absolute inset-0 flex items-center px-6 md:px-12">
                <div>
                  <p className="text-[10px] md:text-xs font-body uppercase tracking-[0.3em] mb-2" style={{ color: `hsl(${theme.cardAccent})` }}>Featured</p>
                  <h3 className="font-heading text-2xl md:text-4xl text-white mb-2">{categories[0].name}</h3>
                  <span className="text-xs font-body uppercase tracking-wider flex items-center gap-1" style={{ color: `hsl(${theme.cardAccent})` }}>Shop Now <ArrowRight size={12} /></span>
                </div>
              </div>
            </Link>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {categories.slice(1).map((cat, index) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group relative overflow-hidden rounded-lg aspect-square">
                <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={400} height={400} quality={80} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
                <div className="absolute bottom-2 left-2 md:bottom-3 md:left-3">
                  <h3 className="font-heading text-xs md:text-sm text-white">{cat.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      );
    }

    // Special: metro has varying spans
    if (layout === "metro") {
      return (
        <div className={tpl.container}>
          {categories.map((cat, index) => {
            const span = index === 0 ? "col-span-2 row-span-2" : index % 5 === 3 ? "col-span-2" : "";
            return (
              <Link key={cat.id} to={`/category/${cat.slug}`} className={`group relative overflow-hidden ${shapeClass} ${span}`}>
                <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={600} height={600} quality={80} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className={`absolute inset-0 bg-gradient-${tpl.overlayGradient}`} />
                <div className="absolute bottom-2 left-2 md:bottom-3 md:left-3"><h3 className="font-heading text-xs md:text-sm text-white">{cat.name}</h3></div>
              </Link>
            );
          })}
        </div>
      );
    }

    // Special: masonry-alt has varying spans
    if (layout === "masonry-alt") {
      return (
        <div className={tpl.container}>
          {categories.map((cat, index) => {
            const tall = index % 4 === 0;
            return (
              <Link key={cat.id} to={`/category/${cat.slug}`} className={`group relative overflow-hidden ${shapeClass}`} style={{ aspectRatio: tall ? "3/5" : "3/4" }}>
                <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={500} height={tall ? 800 : 600} quality={80} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className={`absolute inset-0 bg-gradient-${tpl.overlayGradient}`} />
                <div className="absolute bottom-2 left-2 md:bottom-3 md:left-3"><h3 className="font-heading text-xs md:text-base text-white">{cat.name}</h3></div>
              </Link>
            );
          })}
        </div>
      );
    }

    // Generic template render
    return (
      <div className={tpl.container}>
        {categories.map((cat, index) => {
          // Diamond shape: rotated container
          if (isDiamond) {
            return (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group flex flex-col items-center gap-3">
                <div className="relative w-20 h-20 md:w-28 md:h-28 overflow-hidden rotate-45 border transition-all duration-500 group-hover:scale-110" style={{ borderColor: `hsl(${theme.borderColor})`, borderRadius: "8px" }}>
                  <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={224} height={224} quality={80} className="w-full h-full object-cover -rotate-45 scale-[1.42]" />
                </div>
                <span className="text-[12px] md:text-[14px] font-heading font-bold tracking-normal uppercase text-center" style={{ color: "hsl(0 0% 8%)" }}>{cat.name}</span>
              </Link>
            );
          }

          // Circle/bubble shapes
          if (tpl.shape === "circle") {
            const s = SIZE_MAP[getSize(cat)] || SIZE_MAP.medium;
            return (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group flex flex-col items-center gap-2.5">
                <div className={`relative ${s.circle} rounded-lg overflow-hidden border-2 transition-all duration-500 group-hover:scale-105`} style={{ borderColor: `hsl(${theme.borderColor})` }}>
                  <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={s.circleImg} height={s.circleImg} quality={85} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <span className={`${s.text} font-heading font-bold tracking-normal uppercase text-center`} style={{ color: "hsl(0 0% 8%)" }}>{cat.name}</span>
              </Link>
            );
          }

          // Hexagon shape (clip-path)
          if (tpl.shape === "hexagon") {
            return (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group flex flex-col items-center gap-2">
                <div className="relative w-20 h-20 md:w-28 md:h-28 overflow-hidden transition-transform duration-500 group-hover:scale-110" style={{ clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }}>
                  <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={224} height={224} quality={80} className="w-full h-full object-cover" />
                </div>
                <span className="text-[12px] md:text-[14px] font-heading font-bold tracking-normal uppercase text-center" style={{ color: "hsl(0 0% 8%)" }}>{cat.name}</span>
              </Link>
            );
          }

          // Pill shape (tag-like with image)
          if (tpl.shape === "pill" && !isTagWall) {
            return (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group relative overflow-hidden w-36 md:w-48 h-16 md:h-20 rounded-full transition-all duration-300 group-hover:scale-105" style={{ border: `1px solid hsl(${theme.borderColor})` }}>
                <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={384} height={160} quality={75} className="w-full h-full object-cover" />
                <div className={`absolute inset-0 bg-gradient-${tpl.overlayGradient}`} />
                <div className="absolute inset-0 flex items-center px-4">
                  <h3 className="font-heading text-xs md:text-sm text-white">{cat.name}</h3>
                </div>
              </Link>
            );
          }

          // Postcard: bordered card with image + text below
          if (layout === "postcard") {
            return (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group">
                <div className={`overflow-hidden ${shapeClass} mb-3 border-4 transition-all duration-300 group-hover:shadow-lg`} style={{ borderColor: `hsl(${theme.borderColor})`, aspectRatio: "4/3" }}>
                  <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={400} height={300} quality={85} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <h3 className="font-heading font-bold text-[13px] md:text-[15px] text-center tracking-normal uppercase" style={{ color: "hsl(0 0% 8%)" }}>{cat.name}</h3>
              </Link>
            );
          }

          // Stamp: image in box with text below
          if (layout === "stamp") {
            return (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group flex flex-col items-center gap-2">
                <div className={`overflow-hidden ${shapeClass} border-2 transition-all duration-300 group-hover:shadow-lg`} style={{ borderColor: `hsl(${theme.borderColor})`, aspectRatio: "1/1", width: "100%" }}>
                  <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={200} height={200} quality={80} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <span className="text-[12px] md:text-[14px] font-heading font-bold tracking-normal uppercase text-center" style={{ color: "hsl(0 0% 8%)" }}>{cat.name}</span>
              </Link>
            );
          }

          // Shelf: image with text below
          if (layout === "shelf") {
            return (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group text-center">
                <div className={`overflow-hidden ${shapeClass} transition-all duration-500 group-hover:-translate-y-1`} style={{ aspectRatio: "4/5", boxShadow: `0 6px 20px hsl(${theme.cardAccent} / 0.1)` }}>
                  <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={400} height={500} quality={85} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <h3 className="font-heading font-bold text-[13px] md:text-[15px] mt-3 tracking-normal uppercase text-center" style={{ color: "hsl(0 0% 8%)" }}>{cat.name}</h3>
              </Link>
            );
          }

          // Default template: image with overlay + text
          return (
            <Link key={cat.id} to={`/category/${cat.slug}`} className={`${tpl.itemWrapper} relative overflow-hidden ${shapeClass} transition-all duration-300 hover:scale-[1.02]`} style={isHorizontal ? {} : { aspectRatio: tpl.aspect === "aspect-square" ? "1/1" : tpl.aspect === "aspect-[3/4]" ? "3/4" : tpl.aspect === "aspect-[4/5]" ? "4/5" : tpl.aspect === "aspect-[2/3]" ? "2/3" : tpl.aspect === "aspect-[4/3]" ? "4/3" : tpl.aspect === "aspect-[2/1]" ? "2/1" : undefined }}>
              <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={500} height={500} quality={80} className={`w-full ${isHorizontal ? tpl.aspect + " object-cover" : "h-full object-cover"} group-hover:scale-110 transition-transform duration-500`} />
              {!isNoOverlay && <div className={`absolute inset-0 bg-gradient-${tpl.overlayGradient}`} />}
              <div className={getTextPosClass()}>
                <div className={tpl.textPos === "center" ? "text-center" : ""}>
                  <h3 className="font-heading text-xs md:text-sm text-white drop-shadow-md">{cat.name}</h3>
                  {tpl.showCta && (
                    <p className="text-[9px] md:text-[10px] font-body uppercase tracking-wider flex items-center gap-1 mt-0.5" style={{ color: `hsl(${theme.cardAccent})` }}>
                      Shop Now <ArrowRight size={10} />
                    </p>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    );
  };

  return (
    <section id="categories" className="py-6 md:py-10 lg:py-12 mx-2 sm:mx-4 my-2 sm:my-3 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[hsl(var(--baby-mint))] via-[hsl(var(--baby-cream))] to-[hsl(var(--baby-pink))]">
      <div className="mx-auto max-w-[1800px] px-4">
        {sectionHeader}

        {compactPreview}

        {/* 1. CIRCLES */}
        {false && showAll && layout === "circles" && (
          <div className={showAll ? "grid grid-cols-3 gap-4 md:flex md:flex-wrap md:justify-center md:gap-8 lg:gap-12" : "grid grid-cols-3 gap-4 md:grid-cols-8 md:gap-4 lg:gap-5"}>
            {categories.map((cat, index) => {
              const s = SIZE_MAP[getSize(cat)] || SIZE_MAP.medium;
              return (
                <Link key={cat.id} to={`/category/${cat.slug}`} className="group flex flex-col items-center gap-2.5 md:gap-3">
                  <div className={`relative ${s.circle} rounded-lg overflow-hidden border-2 transition-all duration-500 group-hover:scale-105`} style={{ borderColor: `hsl(${theme.borderColor})` }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = `hsl(${theme.cardAccent})`; e.currentTarget.style.boxShadow = `0 0 25px hsl(${theme.cardAccent} / 0.3)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = `hsl(${theme.borderColor})`; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={s.circleImg} height={s.circleImg} quality={85} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <span className={`${s.text} font-heading font-bold tracking-normal uppercase text-center leading-tight transition-colors duration-300`} style={{ color: "hsl(0 0% 8%)" }}>{cat.name}</span>
                </Link>
              );
            })}
          </div>
        )}

        {/* 2. CARDS */}
        {false && showAll && layout === "cards" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5">
            {categories.map((cat, index) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group relative overflow-hidden aspect-[3/4] transition-transform duration-300 hover:scale-[1.02]" style={{ borderRadius: theme.btnRadius }}>
                <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={400} height={500} quality={85} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                  <h3 className="font-heading text-sm md:text-base text-white">{cat.name}</h3>
                  <p className="text-[10px] md:text-xs mt-0.5 uppercase tracking-wider" style={{ color: `hsl(${theme.cardAccent})` }}>Shop Now →</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* 3. GRID */}
        {false && showAll && layout === "grid" && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {categories.map((cat, index) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className={`group relative overflow-hidden rounded-md transition-transform duration-300 hover:scale-[1.01] ${index === 0 ? "md:col-span-2 md:row-span-2" : ""}`} style={{ aspectRatio: index === 0 ? "1/1" : "16/10" }}>
                <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={index === 0 ? 800 : 500} height={index === 0 ? 800 : 320} quality={85} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-5"><h3 className="font-heading text-sm md:text-lg text-white">{cat.name}</h3></div>
                <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[9px] md:text-[10px] font-body uppercase tracking-wider" style={{ backgroundColor: `hsl(${theme.badgeBg})`, color: `hsl(${theme.badgeText})` }}>Explore</div>
              </Link>
            ))}
          </div>
        )}

        {/* 4. SCROLL */}
        {false && showAll && layout === "scroll" && (
          <div className="flex gap-4 md:gap-6 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4 snap-x snap-mandatory">
            {categories.map((cat, index) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group flex-shrink-0 w-40 md:w-56 snap-start">
                <div className="aspect-[4/5] overflow-hidden mb-3" style={{ borderRadius: theme.btnRadius }}>
                  <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={400} height={500} quality={85} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <p className="text-xs md:text-sm font-body text-center tracking-wider uppercase" style={{ color: `hsl(${theme.categoryText})` }}>{cat.name}</p>
              </Link>
            ))}
          </div>
        )}

        {/* 5. BANNER */}
        {false && showAll && layout === "banner" && (
          <div className="space-y-3 md:space-y-4">
            {categories.map((cat, index) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group block relative overflow-hidden h-32 md:h-48" style={{ borderRadius: theme.btnRadius }}>
                <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={1200} height={400} quality={80} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, hsl(${theme.categoryBg} / 0.85), hsl(${theme.categoryBg} / 0.3), transparent)` }} />
                <div className="absolute inset-0 flex items-center px-6 md:px-12">
                  <div>
                    <h3 className="font-heading text-xl md:text-3xl mb-1" style={{ color: `hsl(${theme.categoryText})` }}>{cat.name}</h3>
                    <p className="text-xs font-body uppercase tracking-widest flex items-center gap-1.5" style={{ color: `hsl(${theme.cardAccent})` }}>Explore Collection <ArrowRight size={12} /></p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* 6. MINIMAL */}
        {false && showAll && layout === "minimal" && (
          <div className="max-w-2xl mx-auto divide-y" style={{ borderColor: `hsl(${theme.borderColor})` }}>
            {categories.map((cat, index) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group flex items-center justify-between py-5 md:py-7 transition-colors">
                <div className="flex items-center gap-4 md:gap-6">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-sm overflow-hidden flex-shrink-0">
                    <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={128} height={128} quality={80} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-heading text-base md:text-xl tracking-wide" style={{ color: `hsl(${theme.categoryText})` }}>{cat.name}</h3>
                </div>
                <ArrowRight size={18} style={{ color: `hsl(${theme.mutedText})` }} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            ))}
          </div>
        )}

        {/* 7. MOSAIC */}
        {false && showAll && layout === "mosaic" && (
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-3 auto-rows-[120px] md:auto-rows-[180px]">
            {categories.map((cat, index) => {
              const span = index % 5 === 0 ? "col-span-2 row-span-2" : index % 3 === 0 ? "col-span-2" : "";
              return (
                <Link key={cat.id} to={`/category/${cat.slug}`} className={`group relative overflow-hidden ${span}`} style={{ borderRadius: theme.btnRadius }}>
                  <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={600} height={600} quality={80} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
                  <div className="absolute bottom-2 left-2 md:bottom-4 md:left-4"><h3 className="font-heading text-xs md:text-base text-white">{cat.name}</h3></div>
                </Link>
              );
            })}
          </div>
        )}

        {/* 8. FLOATING */}
        {false && showAll && layout === "floating" && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {categories.map((cat, index) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group text-center">
                <div className="relative aspect-square overflow-hidden rounded-2xl transition-all duration-500 group-hover:-translate-y-2" style={{ boxShadow: `0 8px 30px hsl(${theme.cardAccent} / 0.1)` }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 16px 50px hsl(${theme.cardAccent} / 0.25)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 8px 30px hsl(${theme.cardAccent} / 0.1)`; }}
                >
                  <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={400} height={400} quality={85} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <h3 className="font-heading text-sm md:text-base mt-3 md:mt-4 tracking-wide" style={{ color: `hsl(${theme.categoryText})` }}>{cat.name}</h3>
              </Link>
            ))}
          </div>
        )}

        {/* 9. LIST */}
        {false && showAll && layout === "list" && (
          <div className="max-w-3xl mx-auto space-y-4 md:space-y-5">
            {categories.map((cat, index) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="group flex items-center gap-4 md:gap-6 p-3 md:p-4 rounded-lg transition-all duration-300" style={{ backgroundColor: `hsl(${theme.cardBg})`, border: `1px solid hsl(${theme.borderColor})` }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `hsl(${theme.cardAccent})`; e.currentTarget.style.boxShadow = `0 0 20px hsl(${theme.cardAccent} / 0.15)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = `hsl(${theme.borderColor})`; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div className="w-20 h-20 md:w-28 md:h-28 rounded-md overflow-hidden flex-shrink-0">
                  <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={224} height={224} quality={85} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-base md:text-xl mb-1" style={{ color: `hsl(${theme.categoryText})` }}>{cat.name}</h3>
                  <p className="text-[10px] md:text-xs font-body uppercase tracking-widest flex items-center gap-1" style={{ color: `hsl(${theme.cardAccent})` }}>View Collection <ArrowRight size={10} /></p>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* 10. MAGAZINE */}
        {false && showAll && layout === "magazine" && (
          <div className="space-y-4">
            {categories.length > 0 && (
              <Link to={`/category/${categories[0].slug}`} className="group block relative overflow-hidden aspect-[21/9] md:aspect-[3/1]" style={{ borderRadius: theme.btnRadius }}>
                <OptimizedImage src={getCategoryImage(categories[0], 0)} alt={categories[0].name} width={1200} height={400} quality={85} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
                <div className="absolute inset-0 flex items-center px-6 md:px-12">
                  <div>
                    <p className="text-[10px] md:text-xs font-body uppercase tracking-[0.3em] text-white/60 mb-2">Featured</p>
                    <h3 className="font-heading text-2xl md:text-5xl text-white mb-2">{categories[0].name}</h3>
                    <p className="text-xs md:text-sm font-body uppercase tracking-wider flex items-center gap-2" style={{ color: `hsl(${theme.cardAccent})` }}>Explore Now <ArrowRight size={14} /></p>
                  </div>
                </div>
              </Link>
            )}
            {categories.length > 1 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {categories.slice(1).map((cat, index) => (
                  <Link key={cat.id} to={`/category/${cat.slug}`} className="group relative overflow-hidden aspect-square" style={{ borderRadius: theme.btnRadius }}>
                    <OptimizedImage src={getCategoryImage(cat, index)} alt={cat.name} width={400} height={400} quality={80} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-2 left-2 md:bottom-4 md:left-4"><h3 className="font-heading text-xs md:text-sm text-white">{cat.name}</h3></div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 11-50: TEMPLATE-BASED LAYOUTS */}
        {false && showAll && layout in TEMPLATE_LAYOUTS && TEMPLATE_LAYOUTS[layout as string] && renderTemplateLayout(TEMPLATE_LAYOUTS[layout as string])}

        {/* Mobile-only "More / Less" toggle */}
        {hasMore && (
          <div className="mt-4 md:mt-5 flex flex-col items-center">
            <div className="w-full h-px" style={{ backgroundColor: `hsl(${theme.mutedText} / 0.18)` }} />
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="group/more py-2 px-8 inline-flex items-center justify-center gap-5 active:scale-[0.98] transition-transform"
              aria-expanded={showAll}
            >
              <span className="h-px w-6 transition-all duration-500 group-hover/more:w-10" style={{ backgroundColor: `hsl(${theme.cardAccent})` }} />
              <span className="inline-flex items-center gap-2">
                <span className="text-[10px] md:text-[11px] font-body font-semibold tracking-[0.3em] uppercase" style={{ color: `hsl(${theme.categoryText})` }}>
                  {showAll ? "Less Category" : "More Category"}
                </span>
                {showAll ? (
                  <ChevronUp size={12} strokeWidth={2} style={{ color: `hsl(${theme.cardAccent})` }} />
                ) : (
                  <ChevronDown size={12} strokeWidth={2} className="transition-transform duration-300 group-hover/more:translate-y-0.5" style={{ color: `hsl(${theme.cardAccent})` }} />
                )}
              </span>
              <span className="h-px w-6 transition-all duration-500 group-hover/more:w-10" style={{ backgroundColor: `hsl(${theme.cardAccent})` }} />
            </button>
            <div className="w-full h-px" style={{ backgroundColor: `hsl(${theme.mutedText} / 0.18)` }} />
          </div>
        )}
      </div>
    </section>
  );
});

Categories.displayName = "Categories";

export default Categories;
